import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useBusinessId, useTenantParams } from '@/hooks/use-tenant-params';
import { RefreshCw } from 'lucide-react';
import { TERMS } from '@pageboss/shared';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { LockedPanel } from '@/components/gbp/locked-panel';
import { NotConnectedPanel } from '@/components/gbp/not-connected-panel';
import { LocationPicker } from '@/components/gbp/location-picker';
import { GbpLocationCard } from '@/components/gbp/location-card';
import { AgenticCallsCard } from '@/components/gbp/agentic-calls-card';
import { GbpNapDivergenceCard } from '@/components/gbp/nap-divergence-card';
import { GbpPerformanceEmpty, GbpPerformanceTiles } from '@/components/gbp/performance-tiles';
import { GbpDisconnectRow } from '@/components/gbp/disconnect-row';
import { PartErrorBanner } from '@/components/gbp/part-status';
import { GbpPostCompose, type GbpPostEditTarget } from '@/components/gbp/post-compose';
import { GbpPostsList } from '@/components/gbp/posts-list';
import { GbpQuestionsList } from '@/components/gbp/questions-list';
import { buttonVariants } from '@/components/ui/button';
import { RouteTabs } from '@/components/route-tabs';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { useCanManageGbp } from '@/hooks/use-can-manage-gbp';
import {
  useGbpData,
  fetchGbpForSelect,
  invalidateGbp,
  setGbpDisconnected,
  needsUnverifiedBanner,
  type GbpDataState,
} from '@/hooks/use-gbp-data';
import { cn } from '@/lib/utils';
import { gbpConnectHref } from '@/lib/api-paths';
import { gbpConnectError } from '@/lib/gbp-connect-errors';
import { toLocationView } from '@/lib/gbp-adapters';
import { locationRoutes } from '@/lib/route-map';
import type { GbpResponse } from '@/lib/types';

// =====================================================================
// GoogleProfile — "/app/b/:siteId/visibility/google-profile[/content|/map]"
// (WP1 Task 8). Replaces the old flat site-gbp.tsx: same connect / location /
// insights / NAP / posts / Q&A material, split into a Profile | Content | Map
// tab strip and reading its Google data through the shared, single-flight
// `useGbpData` hook instead of its own direct fetch (the SEAM use-overview-
// signals.ts marked for this task). The Reviews list itself does NOT render
// here any more — it moves to Task 9's reputation/reviews.tsx.
//
// Map mounts the untouched `SiteRank` interior (site-rank.tsx) lazily, same
// as before — its own maplibre chunk stays lazy too (site-rank-map.tsx).
// =====================================================================

const SiteRank = lazy(() => import('@/routes/site-rank').then((m) => ({ default: m.SiteRank })));

type ConnectedGbp = Extract<GbpResponse, { state: 'connected' }>;

function activeTabFromPath(businessId: string, pathname: string, siteId: string): 'profile' | 'content' | 'map' {
  if (pathname === locationRoutes.googleProfileContent(businessId, siteId)) return 'content';
  if (pathname === locationRoutes.googleProfileMap(businessId, siteId)) return 'map';
  return 'profile';
}

export function GoogleProfile() {
  const meState = useMe();
  const canManageGbp = useCanManageGbp();
  const { businessId = '', locationId: siteId } = useTenantParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [postEditTarget, setPostEditTarget] = useState<GbpPostEditTarget | null>(null);

  // Same capture-once-on-mount pattern as the pre-Task-8 site-gbp.tsx — see
  // hooks/use-gbp-data.ts's fetchGbpForSelect doc comment for why this must
  // fire exactly once and bypass the hook's own auto-fetch.
  const [selectPending] = useState(() => searchParams.has('select'));
  const [connectError] = useState(() => gbpConnectError(searchParams.get('error')));

  useEffect(() => {
    if (!selectPending && !connectError) return;
    const next = new URLSearchParams(searchParams);
    next.delete('select');
    next.delete('error');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectPending && businessId && siteId) fetchGbpForSelect(businessId, siteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gbp = useGbpData(businessId, siteId ?? '', { skipAutoFetch: selectPending });

  // The `/api/sites/:id` fetch that used to live here existed only to put
  // the business name in the <h1>; the h1 is now the PAGE name (design
  // audit B1) and the business name lives in the sidebar switcher.

  // Same live-region + focus-move contract as the pre-Task-8 screen: a
  // content-replacing success (connect via picker, NAP resolve, disconnect)
  // must announce itself and move focus onto the refreshed body, since the
  // control that triggered it is gone.
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const focusAfterLoad = useRef(false);

  const handleChanged = useCallback(
    (successAnnouncement?: string) => {
      if (!businessId || !siteId) return;
      invalidateGbp(businessId, siteId);
      if (successAnnouncement) {
        setAnnouncement(successAnnouncement);
        focusAfterLoad.current = true;
      }
    },
    [businessId, siteId],
  );

  const handleDisconnected = useCallback(
    (successAnnouncement: string) => {
      if (!businessId || !siteId) return;
      setGbpDisconnected(businessId, siteId);
      setAnnouncement(successAnnouncement);
      focusAfterLoad.current = true;
    },
    [businessId, siteId],
  );

  useEffect(() => {
    if (!focusAfterLoad.current || gbp.kind !== 'ready') return;
    focusAfterLoad.current = false;
    bodyRef.current?.focus();
  }, [gbp]);

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. The tab (and so the title) comes
    // from the URL, which survives the failed fetch. See page-error.tsx.
    const tab = siteId ? activeTabFromPath(businessId, location.pathname, siteId) : 'profile';
    return (
      <PageError
        eyebrow="Visibility"
        title={tab === 'map' ? 'Map ranking' : TERMS.googleProfile}
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready' || !siteId) return null;

  const activeTab = activeTabFromPath(businessId, location.pathname, siteId);

  // Design audit T7: with no Google connection the three tabs all led to the
  // same connect card — three doors onto one room. The strip is hidden for
  // exactly two states, and the operator ruling (2026-08-28) is what draws
  // that line:
  //   - `not_connected` — the state the ruling named. `locked` (a tier gate)
  //     and `pending_selection` (a half-finished pick) KEEP their tabs: both
  //     mean "not yet", not "there is nothing behind these".
  //   - `loading` — added in T14 for the layout shift. Rendering the strip
  //     before the answer arrives, then pulling it away on `not_connected`,
  //     moves live links out from under a cursor that is already there.
  // The sidebar still lists all three either way, and the Map line in
  // OnceConnectedLines links straight to the map (map ranking has never
  // needed a GBP connection — see the unverified-banner note further down).
  const notConnected = gbp.kind === 'ready' && gbp.data.state === 'not_connected';
  const hideTabs = gbp.kind === 'loading' || notConnected;

  return (
    <PageContainer>
      {/* Profile / Content / Map are three pages of one screen, so the h1
          follows the active tab — this is also what keeps the map tab from
          stacking a second header, since site-rank.tsx no longer carries one
          of its own (design audit B1, "Map page stacks a second header"). */}
      <PageHeader
        eyebrow="Visibility"
        title={activeTab === 'map' ? 'Map ranking' : TERMS.googleProfile}
      />

      <p aria-live="polite" role="status" className="sr-only">
        {announcement ?? ''}
      </p>

      {gbp.kind === 'error' ? (
        <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg" role="alert">
          {gbp.message}
        </div>
      ) : null}

      {connectError ? (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning-fg sm:flex-row sm:items-center sm:justify-between"
        >
          <span className="max-w-prose">{connectError.message}</span>
          {connectError.canSwitchAccount && canManageGbp ? (
            <a
              href={gbpConnectHref(businessId, siteId, { switchAccount: true })}
              className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'shrink-0')}
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Try another account
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Profile | Content | Map — same tab-strip idiom as foundation/
          website.tsx's Website/Domain pair. Every tab that is SHOWN is
          reachable (Map has no GBP-connection dependency of its own;
          nav-model.ts lists all three as equal sibling links); `hideTabs`
          above says when the strip is shown at all. */}
      {hideTabs ? null : (
        <RouteTabs
          ariaLabel={`${TERMS.googleProfile} sections`}
          activeKey={activeTab}
          tabs={[
            { key: 'profile', label: 'Profile', to: locationRoutes.googleProfile(businessId, siteId) },
            { key: 'content', label: 'Content', to: locationRoutes.googleProfileContent(businessId, siteId) },
            { key: 'map', label: 'Map', to: locationRoutes.googleProfileMap(businessId, siteId) },
          ]}
        />
      )}

      {activeTab === 'map' ? (
        <>
          {needsUnverifiedBanner(gbp) ? <UnverifiedBanner siteId={siteId} /> : null}
          <Suspense
            fallback={
              <div className="flex flex-col gap-4">
                <Skeleton className="h-28 w-full rounded-xl" />
                <Skeleton className="h-96 w-full rounded-xl" />
              </div>
            }
          >
            <SiteRank />
          </Suspense>
        </>
      ) : (
        <div ref={bodyRef} tabIndex={-1} className="flex flex-col gap-6 focus:outline-none">
          {gbp.kind === 'loading' ? (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-32 w-full rounded-xl" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
          ) : gbp.kind === 'error' ? null : gbp.data.state === 'locked' ? (
            <LockedPanel siteId={siteId} hasConnection={gbp.data.hasConnection} />
          ) : gbp.data.state === 'not_connected' ? (
            <>
              <NotConnectedPanel siteId={siteId} />
              <OnceConnectedLines siteId={siteId} />
            </>
          ) : gbp.data.state === 'pending_selection' ? (
            <LocationPicker candidates={gbp.data.candidates} onChanged={handleChanged} />
          ) : activeTab === 'profile' ? (
            <ProfileTab
              siteId={siteId}
              data={gbp.data}
              onChanged={handleChanged}
              onDisconnected={handleDisconnected}
            />
          ) : (
            <ContentTab
              siteId={siteId}
              data={gbp.data}
              postEditTarget={postEditTarget}
              onEditRequest={setPostEditTarget}
              onChanged={handleChanged}
            />
          )}
        </div>
      )}
    </PageContainer>
  );
}

// The three tabs, described in one place while there is nothing to put in
// them yet (design audit T7). Plain sentences, no card frame — the connect
// card above is the only thing on this screen asking to be clicked.
function OnceConnectedLines({ siteId }: { siteId: string }) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  return (
    <div className="max-w-prose">
      <p className="text-sm text-text-secondary">Once you're connected, this screen has three parts:</p>
      <ul className="mt-3 flex flex-col gap-2">
        <li className="text-sm leading-relaxed text-text-secondary">
          <span className="font-medium text-foreground">Profile</span> — your name, address, phone,
          hours and categories, with a heads-up whenever they stop matching your site.
        </li>
        <li className="text-sm leading-relaxed text-text-secondary">
          <span className="font-medium text-foreground">Content</span> — the posts you publish to
          Google and the questions customers ask, answered from here.
        </li>
        <li className="text-sm leading-relaxed text-text-secondary">
          <span className="font-medium text-foreground">Map</span> — where you turn up on the map
          around your neighborhood, week by week. You can{' '}
          {/* T11/A7: py-1 on an inline link grows the clickable box past
              24 px without changing the line box or breaking the wrap. */}
          <Link to={locationRoutes.googleProfileMap(businessId, siteId)} className="py-1 font-medium text-foreground underline underline-offset-2">
            look at that now
          </Link>{' '}
          — it doesn't wait on Google.
        </li>
      </ul>
    </div>
  );
}

// CONSOLIDATION A6 (binding, WP1 Task 8): the map tab (and any other
// visibility-landing content this task touches) renders a non-blocking
// "unverified" notice whenever the org's GBP connection is not `connected` —
// the map itself keeps working (it has its own tier gate, unrelated to GBP),
// this is purely an honesty nudge with a way to fix it.
function UnverifiedBanner({ siteId }: { siteId: string }) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle bg-surface-sunken px-3 py-2.5"
    >
      <p className="text-sm text-text-secondary">
        Unverified — connect your {TERMS.googleProfile} to verify ownership of this business.
      </p>
      <Link
        to={locationRoutes.googleProfile(businessId, siteId)}
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
      >
        Connect Google
      </Link>
    </div>
  );
}

// Profile tab: connect (handled above)/location/insights/NAP — moved from
// site-gbp.tsx's GbpConnectedPanels, minus the reviews section (Task 9) and
// minus posts/Q&A (Content tab, below). The disconnect footer lives here —
// account-management material, alongside the connection/location facts.
function ProfileTab({
  siteId,
  data,
  onChanged,
  onDisconnected,
}: {
  siteId: string;
  data: ConnectedGbp;
  onChanged: (announcement?: string) => void;
  onDisconnected: (announcement: string) => void;
}) {
  const locationData = data.location.data;
  const reviewsData = data.reviews.data;

  return (
    <>
      {data.napDivergence ? (
        <GbpNapDivergenceCard
          siteId={siteId}
          napDivergence={data.napDivergence}
          placeId={data.connection.placeId}
          onResolved={onChanged}
        />
      ) : null}

      {data.location.ok === false ? (
        <PartErrorBanner
          siteId={siteId}
          what="your listing details"
          error={data.location.error}
          hasStaleData={!!data.location.data}
        />
      ) : null}

      {locationData ? (
        <>
          <GbpLocationCard
            location={toLocationView(locationData, reviewsData)}
            unverified={data.connection.hasVoiceOfMerchant === false}
            staleFetchedAt={data.location.ok === false ? data.location.fetchedAt : undefined}
          />
          <AgenticCallsCard
            location={locationData}
            site={data.site}
            unverified={data.connection.hasVoiceOfMerchant === false}
            napPhoneDiverges={!!data.napDivergence?.fields.some((f) => f.field === 'phone')}
          />
        </>
      ) : (
        <div className="rounded-lg border border-border-subtle bg-card p-6 text-sm text-text-tertiary">
          No listing details yet.
        </div>
      )}

      <section className="rounded-lg border border-border-subtle bg-card p-6">
        {data.insights.ok === false ? (
          <PartErrorBanner
            siteId={siteId}
            what="your performance numbers"
            error={data.insights.error}
            hasStaleData={!!data.insights.data}
          />
        ) : null}
        {data.insights.data ? (
          data.insights.data.hasMetrics ? (
            <GbpPerformanceTiles
              data={data.insights.data}
              fetchedAt={data.insights.ok === false ? data.insights.fetchedAt : undefined}
            />
          ) : (
            <GbpPerformanceEmpty reason="no_metrics" />
          )
        ) : (
          <GbpPerformanceEmpty reason="unavailable" />
        )}
      </section>

      <GbpDisconnectRow siteId={siteId} onDisconnected={onDisconnected} />
    </>
  );
}

// Content tab: posts + Q&A — moved from site-gbp.tsx's GbpConnectedPanels.
function ContentTab({
  siteId,
  data,
  postEditTarget,
  onEditRequest,
  onChanged,
}: {
  siteId: string;
  data: ConnectedGbp;
  postEditTarget: GbpPostEditTarget | null;
  onEditRequest: (target: GbpPostEditTarget | null) => void;
  onChanged: (announcement?: string) => void;
}) {
  return (
    <>
      <section className="rounded-lg border border-border-subtle bg-card p-6">
        <header className="mb-4">
          <h2 className="text-base font-semibold tracking-tight">Recent posts</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Updates, offers, and events you've published to your Google listing.
          </p>
        </header>
        <GbpPostCompose
          siteId={siteId}
          canWrite={data.canWrite}
          editTarget={postEditTarget}
          onEditConsumed={() => onEditRequest(null)}
          onChanged={onChanged}
        />
        <div className="mt-6 border-t border-border-subtle pt-6">
          <GbpPostsList
            posts={data.posts}
            siteId={siteId}
            canWrite={data.canWrite}
            onChanged={onChanged}
            onEditRequest={onEditRequest}
          />
        </div>
      </section>

      <section className="rounded-lg border border-border-subtle bg-card p-6">
        <header className="mb-4">
          <h2 className="text-base font-semibold tracking-tight">Questions & answers</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Questions customers asked on your Google listing. Answers from the owner appear first.
          </p>
        </header>
        <GbpQuestionsList
          questions={data.questions}
          siteId={siteId}
          canWrite={data.canWrite}
          onChanged={onChanged}
        />
      </section>
    </>
  );
}
